import { ReactNode } from "react";

import { css } from "@compiled/react";

import { AtlassianLogo } from "@atlaskit/logo";
import { media } from "@atlaskit/primitives/responsive";
import { N0 } from "@atlaskit/theme/colors";

export const ModalOverlay = ({
    logo,
    secondary,
    children
}: {
    logo?: ReactNode;
    secondary?: ReactNode;
    children: ReactNode;
}) => {
    return (
        <div css={positioningFrameCss}>
            <div css={modalOverlayCss}>
                {logo && (
                    <div css={[logoCss, secondary && leftAlignLogoCss]}>
                        {logo}
                    </div>
                )}
                <div css={contentCss}>
                    <div css={[primaryCss, secondary && leftAlignHeadings]}>
                        {children}
                    </div>
                    {secondary && <div css={secondaryCss}>{secondary}</div>}
                </div>
                <div css={flexGrowStyle} />
                <div css={[footerCss, secondary && rightAlignFooterCss]}>
                    {/* @ts-ignore */}
                    <AtlassianLogo appearance="neutral" size="xxsmall" />
                </div>
            </div>
        </div>
    );
};

// point at which JiraBackground becomes visible
const modalBreakpoint = "@media (min-width: 801px)";
const imageBreakpoint = "@media (min-width: 1100px)";

const positioningFrameCss = css({
    display: "flex",
    justifyContent: "center",
    minHeight: "100vh",
    width: "100vw",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [modalBreakpoint]: {
        alignItems: "center",
        padding: 50
    }
});

const modalOverlayCss = css({
    display: "flex",
    flexDirection: "column",
    gap: 60,
    padding: 30,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [modalBreakpoint]: {
        backgroundColor: N0,
        borderRadius: 4,
        padding: 50
    },
    [media.above.md]: { width: "90%", maxWidth: 888 }
});

const logoCss = css({
    display: "flex",
    justifyContent: "center"
});

const leftAlignLogoCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [imageBreakpoint]: { justifyContent: "start" }
});

const fontSize = {
    xs: "0.75rem", // 12px
    sm: "0.875rem", // 14px
    md: "1rem", // 16px
    lg: "1.25rem", // 20px
    xl: "2.25rem" // 36px
};

const contentCss = css({
    "display": "flex",
    "justifyContent": "center",
    "fontFamily": "var(--atlassian-recommended-system-fonts)",
    "fontSize": fontSize.sm,
    "gap": 50,
    "maxWidth": "100%",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h1": {
        fontSize: fontSize.xl,
        fontWeight: 500,
        lineHeight: 1.15,
        margin: 0
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h2": {
        fontSize: "inherit",
        fontWeight: "normal",
        lineHeight: 1.25,
        margin: ".5rem 0 1.25rem"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h1, h2": {
        textAlign: "center"
    }
});

const leftAlignHeadings = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h1, h2": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [imageBreakpoint]: { textAlign: "left" }
    }
});

const primaryCss = css({
    maxWidth: "100%"
});

const secondaryCss = css({
    display: "none",
    flex: 1,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [imageBreakpoint]: { display: "block" }
});

const footerCss = css({
    display: "flex",
    justifyContent: "center",
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "var(--N60, #B3BAC5)",
    fontSize: "11px",
    fontWeight: "700"
});

const rightAlignFooterCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [imageBreakpoint]: { justifyContent: "end" }
});

const flexGrowStyle = css({
    flexGrow: "2",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [modalBreakpoint]: {
        flexGrow: "1"
    }
});
